import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import MarketingSiteLayout from '../../marketing/MarketingSiteLayout'
import { publicFaqExtendedItems } from '../../marketing/publicFaqExtended'
import { supportGuideManualFallbackItems } from '../../content/supportGuideManuals'
import { PUBLIC_INQUIRY_PATH } from '../../marketing/marketingNav'
import { api, absoluteApiV1Path } from '../../lib/api'

/** DB가 내려주는 상대 경로(public/...) 또는 절대 URL을 브라우저에서 열 수 있는 주소로 만듭니다. */
function resolveManualLink(href) {
  if (!href) return null
  const s = String(href).trim()
  if (!s) return null
  if (/^https?:\/\//i.test(s)) return s
  const path = s.startsWith('/') ? s.slice(1) : s
  if (path.startsWith('public/')) {
    return absoluteApiV1Path(path)
  }
  return s
}

function unwrapData(res) {
  const p = res?.data
  if (p?.data !== undefined) return p.data
  return p ?? null
}

function ManualAction({ href, children, variant }) {
  if (!href) {
    return (
      <span className="inline-flex cursor-not-allowed items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-400">
        준비 중
      </span>
    )
  }
  const cls =
    variant === 'primary'
      ? 'inline-flex items-center justify-center rounded-lg bg-brand-navy px-3 py-2 text-xs font-bold text-white hover:bg-brand-navy/90'
      : 'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-800 hover:border-sky-300 hover:bg-sky-50'
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {children}
    </a>
  )
}

export default function SupportGuidePage() {
  const [manualItems, setManualItems] = useState(supportGuideManualFallbackItems)
  const [manualsLoading, setManualsLoading] = useState(true)
  const [manualsErr, setManualsErr] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setManualsErr('')
      setManualsLoading(true)
      try {
        const res = await api.get('/public/support-guide/manuals')
        const data = unwrapData(res)
        if (cancelled) return
        if (Array.isArray(data) && data.length > 0) {
          setManualItems(
            data.map((row) => ({
              id: row.slug || String(row.id),
              title: row.title,
              summary: row.summary,
              viewUrl: resolveManualLink(row.viewUrl),
              downloadUrl: resolveManualLink(row.downloadUrl),
            })),
          )
        } else {
          setManualItems(supportGuideManualFallbackItems)
        }
      } catch {
        if (!cancelled) {
          setManualsErr('매뉴얼 목록을 불러오지 못해 기본 안내를 표시합니다.')
          setManualItems(supportGuideManualFallbackItems)
        }
      } finally {
        if (!cancelled) setManualsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <MarketingSiteLayout>
      <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
        <nav className="text-sm text-slate-500">
          <Link to="/support" className="hover:text-brand-navy">
            고객센터
          </Link>
          <span className="mx-2">/</span>
          <span className="text-slate-800">이용 가이드</span>
        </nav>

        <h1 className="mt-6 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">이용 가이드</h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
          메뉴별 매뉴얼은 서버에 포함된 HTML로 제공되며, PDF로 저장할 수 있습니다. 자주 묻는 질문은 아래에서 바로 확인하실 수 있고,{' '}
          <Link to="/support/faq" className="font-semibold text-brand-navy hover:underline">
            FAQ 전용 페이지
          </Link>
          에서도 동일 내용을 보실 수 있어요.
        </p>

        {/* 메뉴별 매뉴얼 */}
        <section className="mt-14">
          <h2 className="text-xl font-black text-slate-900">메뉴별 매뉴얼</h2>
          <p className="mt-2 text-sm text-slate-600">
            기본 매뉴얼은 <code className="rounded bg-slate-100 px-1">docs/98_manual/file</code> 원본을 빌드에 포함한 HTML입니다. 운영관리
            &gt; 기타관리 &gt; <strong>매뉴얼 관리</strong>에서 외부 URL로 덮어쓰거나 문구를 바꿀 수 있습니다(보기·PDF URL이 비어 있을 때만
            내부 HTML·PDF가 적용됩니다).
          </p>
          {manualsErr ? <p className="mt-2 text-sm text-amber-700">{manualsErr}</p> : null}
          {manualsLoading ? <p className="mt-4 text-sm text-slate-500">매뉴얼 목록을 불러오는 중…</p> : null}
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {manualItems.map((item) => (
              <li
                key={item.id}
                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-sky-200"
              >
                <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">{item.summary}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <ManualAction href={item.viewUrl} variant="primary">
                    HTML 보기 (새 창)
                  </ManualAction>
                  <ManualAction href={item.downloadUrl} variant="secondary">
                    PDF 다운로드
                  </ManualAction>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="text-xl font-black text-slate-900">자주 묻는 질문</h2>
            <Link to="/support/faq" className="text-sm font-semibold text-brand-navy hover:underline">
              FAQ 페이지로만 보기 →
            </Link>
          </div>
          <div className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {publicFaqExtendedItems.map((item) => (
              <div key={item.question} className="px-6 py-6 sm:px-8">
                <h3 className="text-base font-bold text-slate-900">{item.question}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-14 rounded-2xl border border-slate-200 bg-slate-50 p-8">
          <h2 className="text-base font-bold text-slate-900">원하는 내용이 없나요?</h2>
          <p className="mt-2 text-sm text-slate-600">
            가입 전 도입 문의는 공개 문의, 회원은 로그인 후 고객문의를 이용해 주세요.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to={PUBLIC_INQUIRY_PATH}
              className="inline-flex rounded-lg bg-brand-navy px-4 py-2 text-sm font-bold text-white hover:bg-brand-navy/90"
            >
              문의하기 (공개)
            </Link>
            <Link
              to="/support/inquiries"
              className="inline-flex rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-800 hover:border-sky-300"
            >
              고객문의 (로그인)
            </Link>
          </div>
        </div>
      </div>
    </MarketingSiteLayout>
  )
}
